"use client";

import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  useTheme,
  alpha,
  Chip,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import {
  Email,
  LocationOn,
  AccessTime,
  Business,
  Send,
  Public,
  Phone,
} from "@mui/icons-material";

export const runtime = "edge";

const industries = [
  "Ecommerce",
  "FinTech",
  "Startups",
  "Agencies",
  "SMEs",
  "Healthcare",
  "Education",
  "Logistics",
];

const serviceAreas = [
  {
    region: "Africa",
    countries: ["Ghana", "Nigeria", "Kenya", "South Africa"],
  },
  { region: "Europe", countries: ["Netherlands", "UK", "Germany", "France"] },
  { region: "North America", countries: ["United States", "Canada"] },
  { region: "Global", countries: ["Remote Support Everywhere)"] },
];

export default function Contact() {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    industry: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    try {
      // In a real application, you would send this data to your backend
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setSnackbar({
        open: true,
        message:
          "Thank you for your message! We'll get back to you within 24 hours.",
        severity: "success",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        company: "",
        industry: "",
        subject: "",
        message: "",
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again or email us directly.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 6,
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3rem" },
                fontWeight: 800,
                color: theme.palette.primary.main,
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Let’s Automate Your Business – Book a Free Discovery Call.
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              We serve European clients remotely, no matter your time-zone, we
              adapt. Choose a convenient time, share your biggest workflow
              challenge, and let’s explore how to remove repetitive tasks.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Introduction Section */}
        <Box sx={{ mb: 6, textAlign: "center" }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.2rem",
              lineHeight: 1.8,
              color: "text.secondary",
              maxWidth: 800,
              mx: "auto",
              mb: 4,
            }}
          >
            At LinXar Labs, we help businesses in Ghana and beyond embrace
            automation, IT support, and digital transformation. Whether
            you&apos;re a startup in Accra, a retail brand in Amsterdam, or a
            tech agency in New York, our team is here to support you.
          </Typography>

          {/* Service Areas */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, color: theme.palette.primary.main }}
            >
              🗺️ Serving Clients Worldwide
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {serviceAreas.map((area, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {area.region}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {area.countries.join(", ")}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: "sticky", top: 100 }}>
              {/* Contact Details */}
              <Card
                sx={{
                  mb: 4,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 4,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 700, color: theme.palette.primary.main }}
                  >
                    Contact Details
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                    >
                      <LocationOn
                        sx={{
                          color: theme.palette.primary.main,
                          mr: 2,
                          mt: 0.5,
                        }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Operating From
                        </Typography>
                        <Typography color="text.secondary">
                          Accra, Ghana – working with clients across Africa,
                          Europe, and North America
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Email
                        sx={{ color: theme.palette.primary.main, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Email
                        </Typography>
                        <Typography color="text.secondary">
                          info@linxarlabs.com
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Phone
                        sx={{ color: theme.palette.primary.main, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Phone
                        </Typography>
                        <Typography color="text.secondary">
                          +233 20 033 5078
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Business
                        sx={{ color: theme.palette.primary.main, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Industries We Serve
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {industries.map((industry, index) => (
                            <Chip
                              key={index}
                              label={industry}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: alpha(
                                  theme.palette.primary.main,
                                  0.3
                                ),
                                color: "text.primary",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccessTime
                        sx={{ color: theme.palette.primary.main, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Response Time
                        </Typography>
                        <Typography color="text.secondary">
                          Within 24 hours (business days)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Global Presence */}
              <Card
                sx={{
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                }}
              >
                <CardContent sx={{ p: 4, textAlign: "center" }}>
                  <Public
                    sx={{
                      fontSize: 48,
                      color: theme.palette.secondary.main,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Ghana-based, Globally Connected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your international partner for IT support & automation, from
                    Accra to Amsterdam to New York.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* Contact Form */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 1,
                }}
              >
                Let&apos;s Start Your Project
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                Tell us about your needs and we&apos;ll get back to you within a
                day.
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      variant="outlined"
                      helperText="E.g., Ecommerce, FinTech, Startup, etc."
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      helperText="Brief description of how we can help you"
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={6}
                      variant="outlined"
                      helperText="Tell us about your project, challenges, or questions"
                    />
                  </Grid>
                  <Grid size={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? null : <Send />}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        minWidth: 200,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading ? "Sending..." : "Send Message"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Local SEO Keywords - Hidden from users but visible to search engines */}
              <Box sx={{ mt: 4, opacity: 0, height: 0, overflow: "hidden" }}>
                <Typography variant="body2">
                  IT Support in Ghana, Automation solutions for Ghanaian
                  businesses, Helping startups across Accra, Kumasi, and beyond,
                  Web development Ghana, Digital transformation Africa, AI
                  automation Europe, IT solutions United States, Tech support
                  Netherlands, Business automation UK
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Additional Local & International Context */}
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                p: 4,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 4,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, color: theme.palette.primary.main }}
              >
                🚀 For Startups & SMEs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Whether you&apos;re a growing business in Ghana looking to
                automate operations, or an international startup needing
                scalable tech solutions, we understand the unique challenges you
                face and can help you build for scale.
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                p: 4,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                borderRadius: 4,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, color: theme.palette.secondary.main }}
              >
                🌍 International Projects
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We&apos;ve successfully delivered projects across time zones and
                continents. Our remote collaboration tools and communication
                processes ensure seamless project delivery regardless of your
                location.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity as "success" | "error"}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "LinXar Labs",
            url: "https://linxarlabs.com",
            logo: "https://linxarlabs.com/logo.png",
            description:
              "Global partner in automation and IT solutions, based in Ghana serving clients worldwide",
            email: "info@linxarlabs.com",
            telephone: "+233-XXX-XXXXXX",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Accra",
              addressCountry: "GH",
            },
            areaServed: ["GH", "NG", "KE", "ZA", "NL", "GB", "DE", "US", "CA"],
            sameAs: [
              "https://linkedin.com/company/linxarlabs",
              "https://twitter.com/linxarlabs",
            ],
          }),
        }}
      />
    </Box>
  );
}
